import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, User } from 'lucide-react';
import { Button } from '../ui/button';
import { userAuth } from '../../services/userAuth';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'How It Works', path: '/how-it-works' },
  { name: 'Pricing', path: '/pricing' },
  { name: 'Schools', path: '/schools' },
  { name: 'Careers', path: '/careers' },
  { name: 'Contact', path: '/contact' },
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_student-stash-1/artifacts/u55l23lh_Student%20STORE%20IT%20Logo.png";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60"
      data-testid="navbar"
    >
      <div className="section-container">
        <div className="flex h-16 md:h-20 items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <img 
              src={LOGO_URL}
              alt="Student Store It - Campus Storage"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(link.path)
                    ? 'text-navy bg-gray-100'
                    : 'text-gray-600 hover:text-navy hover:bg-gray-50'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons - Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:+15407654298" className="flex items-center gap-2 text-sm text-gray-600 hover:text-navy">
              <Phone className="w-4 h-4" />
              (540) 765-4298
            </a>
            {userAuth.isAuthenticated() ? (
              <Link to="/account" data-testid="nav-account-btn">
                <Button variant="outline" className="border-[#1a2e4e] text-[#1a2e4e] hover:bg-[#1a2e4e]/5">
                  <User className="w-4 h-4 mr-1.5" /> My Account
                </Button>
              </Link>
            ) : (
              <Link to="/login" data-testid="nav-login-btn">
                <Button variant="ghost" className="text-[#1a2e4e] hover:bg-slate-100">Sign In</Button>
              </Link>
            )}
            <Link to="/contact">
              <Button 
                className="btn-primary"
                data-testid="nav-reserve-btn"
              >
                Reserve Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-md text-gray-600 hover:text-navy hover:bg-gray-100"
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            className="lg:hidden py-4 border-t mobile-menu-enter"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-3 text-base font-medium rounded-md transition-colors ${
                    isActive(link.path)
                      ? 'text-navy bg-gray-100'
                      : 'text-gray-600 hover:text-navy hover:bg-gray-50'
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 px-4 space-y-3">
                <a 
                  href="tel:+15407654298" 
                  className="flex items-center justify-center gap-2 py-3 text-navy font-medium border border-gray-200 rounded-md"
                >
                  <Phone className="w-4 h-4" />
                  (540) 765-4298
                </a>
                {userAuth.isAuthenticated() ? (
                  <Link to="/account" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full border-[#1a2e4e] text-[#1a2e4e]" data-testid="mobile-account-btn">
                      <User className="w-4 h-4 mr-1.5" /> My Account
                    </Button>
                  </Link>
                ) : (
                  <Link to="/login" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full" data-testid="mobile-login-btn">Sign In</Button>
                  </Link>
                )}
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  <Button 
                    className="btn-primary w-full"
                    data-testid="mobile-reserve-btn"
                  >
                    Reserve Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
